import { useState, useEffect } from "react";
import { useAuthentication, useOrders, useCart } from "hooks";
import { useNavigate } from "react-router-dom";
import { databasePromise } from "configuration";
import { doc, getDoc } from "firebase/firestore";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Skeleton,
  Card,
  CardContent,
  Stack,
  Button,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Rating,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LoginIcon from "@mui/icons-material/Login";
import ReplayIcon from "@mui/icons-material/Replay";
import StarIcon from "@mui/icons-material/Star";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { BasePage, NotificationTypography } from "components";
import { ChatButton } from "components/buttons";
import { reviewService } from "services";
import { useSnack } from "hooks";

const ORDER_STEPS = ["Placed", "Confirmed", "Preparing", "Ready", "Completed"];
const STATUS_STEP_INDEX = {
  pending: 0,
  confirmed: 1,
  preparing: 2,
  ready: 3,
  completed: 4,
};

// Only prompt for a review within 7 days of order completion
const REVIEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function formatDate(val) {
  if (!val) return "";
  const date = val?.toDate ? val.toDate() : new Date(val);
  return date.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
}

function orderAge(val) {
  if (!val) return Infinity;
  const date = val?.toDate ? val.toDate() : new Date(val);
  return Date.now() - date.getTime();
}

export function OrdersPage() {
  const { user, isAuthenticated } = useAuthentication();
  const { orders, loading } = useOrders(user?.uid);
  const navigate = useNavigate();

  // Track restaurants reviewed this session — prevents prompting on multiple
  // orders from the same restaurant. Resets on page reload which is fine;
  // a proper cross-session solution would query the reviews collection.
  const [reviewedRestaurantIds, setReviewedRestaurantIds] = useState(new Set());

  if (!isAuthenticated) {
    return (
      <BasePage>
        <NotificationTypography
          color="info"
          Icon={LoginIcon}
          title="Sign in required"
          message="Please sign in to view your orders."
          showActions={false}
        />
      </BasePage>
    );
  }

  return (
    <BasePage>
      <Typography variant="h6" mb={3}>Orders</Typography>

      {loading ? (
        <OrdersSkeleton />
      ) : orders.length === 0 ? (
        <NotificationTypography
          color="info"
          Icon={ReceiptLongIcon}
          title="No orders yet"
          message="Place your first order to see it here."
          showActions={false}
        />
      ) : (
        <Stack spacing={2}>
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              userId={user?.uid}
              alreadyReviewed={reviewedRestaurantIds.has(order.restaurantId)}
              onReviewed={(restaurantId) =>
                setReviewedRestaurantIds((prev) => new Set([...prev, restaurantId]))
              }
            />
          ))}
        </Stack>
      )}
    </BasePage>
  );
}

function OrderCard({ order, userId, alreadyReviewed, onReviewed }) {
  const { addToCart, replaceCart, cart } = useCart();
  const { openSnack } = useSnack();
  const navigate = useNavigate();
  const [reordering, setReordering] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  // vendorUserId is stored on the order as restaurantUserId for new orders.
  // For older orders that lack it, fetch it once from the restaurant document.
  const [vendorUserId, setVendorUserId] = useState(order.restaurantUserId || null);

  useEffect(() => {
    if (vendorUserId || !order.restaurantId) return;
    databasePromise.then(async (db) => {
      try {
        const snap = await getDoc(doc(db, "restaurants", order.restaurantId));
        if (snap.exists()) setVendorUserId(snap.data().userId || null);
      } catch {
        // Silent fail — chat button just won't show
      }
    });
  }, [order.restaurantId, vendorUserId]);

  const isCancelled = order.status === "cancelled";
  const isCompleted = order.status === "completed";
  const isActive = !isCancelled && !isCompleted;
  const stepIndex = STATUS_STEP_INDEX[order.status] ?? 0;

  // Show review prompt: completed + within 7 days + not reviewed this session
  const withinReviewWindow = orderAge(order.updatedAt || order.createdAt) <= REVIEW_WINDOW_MS;
  const showReviewPrompt = isCompleted && withinReviewWindow && !alreadyReviewed;

  const handleReorder = async () => {
    if (!order.items?.length) return;
    setReordering(true);
    try {
      // Validate each deal still exists and is available before adding to cart
      const db = await databasePromise;
      const unavailableStatuses = new Set(["rejected", "disabled", "pendingDelete", "deleted"]);

      const validationResults = await Promise.all(
        order.items.map(async (item) => {
          if (!item.dealId) return { item, available: false };
          try {
            const snap = await getDoc(doc(db, "deals", item.dealId));
            if (!snap.exists()) return { item, available: false };
            const data = snap.data();
            if (unavailableStatuses.has(data.status)) return { item, available: false };
            // Use current price from the deal doc if available
            return { item: { ...item, price: data.price ?? item.price }, available: true };
          } catch {
            return { item, available: false };
          }
        })
      );

      const available = validationResults.filter((r) => r.available);
      const unavailable = validationResults.filter((r) => !r.available);

      if (available.length === 0) {
        openSnack("None of these items are available anymore", "error");
        return;
      }

      const hasConflict = cart.restaurantId && cart.restaurantId !== order.restaurantId;
      const [first, ...rest] = available;

      if (hasConflict) {
        replaceCart(
          first.item.dealId,
          order.restaurantId,
          { name: first.item.name, price: first.item.price, imageUrl: first.item.imageUrl },
          { name: order.restaurantName, logoUrl: order.restaurantLogoUrl },
        );
        rest.forEach(({ item }) => {
          addToCart(
            item.dealId,
            order.restaurantId,
            { name: item.name, price: item.price, imageUrl: item.imageUrl },
            null,
          );
        });
      } else {
        const restaurantMeta = { name: order.restaurantName, logoUrl: order.restaurantLogoUrl };
        addToCart(
          first.item.dealId,
          order.restaurantId,
          { name: first.item.name, price: first.item.price, imageUrl: first.item.imageUrl },
          restaurantMeta,
        );
        rest.forEach(({ item }) => {
          addToCart(
            item.dealId,
            order.restaurantId,
            { name: item.name, price: item.price, imageUrl: item.imageUrl },
            null,
          );
        });
      }

      if (unavailable.length > 0) {
        const names = unavailable.map((r) => r.item.name).filter(Boolean).join(", ");
        openSnack(
          `${available.length} item${available.length !== 1 ? "s" : ""} added to cart. No longer available: ${names}`,
          "warning"
        );
      } else {
        openSnack("Items added to cart", "success");
      }
    } catch (err) {
      console.error("Reorder failed:", err);
      openSnack("Failed to add items to cart", "error");
    } finally {
      setReordering(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewRating) return;
    setSubmittingReview(true);
    try {
      await reviewService.createReview({
        restaurantId: order.restaurantId,
        dealId: null,
        userId,
        rating: reviewRating,
        description: reviewText.trim(),
        orderId: order.id,
      });
      openSnack("Review submitted — thanks!", "success");
      setReviewOpen(false);
      setReviewRating(0);
      setReviewText("");
      // Mark restaurant as reviewed so prompt hides on all other orders
      // from the same restaurant in this session
      onReviewed(order.restaurantId);
    } catch (err) {
      console.error("Review failed:", err);
      openSnack("Failed to submit review", "error");
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor:
          order.status === "ready" ? "success.main" :
          order.status === "pending" ? "warning.main" : "divider",
        borderWidth: (order.status === "ready" || order.status === "pending") ? 2 : 1,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar
            src={order.restaurantLogoUrl}
            variant="rounded"
            sx={{ width: 48, height: 48, flexShrink: 0 }}
            alt={order.restaurantName}
          >
            {!order.restaurantLogoUrl && order.restaurantName?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {order.restaurantName || "Restaurant"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(order.createdAt)} · #{order.id.slice(-6).toUpperCase()}
            </Typography>
          </Box>
          <Chip
            label={order.status === "pending" ? "Waiting for confirmation" : order.status}
            color={
              order.status === "ready" ? "success" :
              order.status === "pending" ? "warning" :
              order.status === "confirmed" || order.status === "preparing" ? "info" :
              order.status === "completed" ? "default" : "error"
            }
            size="small"
            sx={{ textTransform: "capitalize", flexShrink: 0 }}
          />
        </Box>

        {/* Status stepper — active orders only */}
        {isActive && (
          <Box sx={{ mb: 2 }}>
            <Stepper activeStep={stepIndex} alternativeLabel>
              {ORDER_STEPS.slice(0, -1).map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>
        )}

        {/* Prep time */}
        {(order.status === "pending" || order.status === "confirmed") && order.estimatedPrepTime && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Est. ready in {order.estimatedPrepTime}
            </Typography>
          </Box>
        )}

        {/* Ready banner */}
        {order.status === "ready" && (
          <Box sx={{ bgcolor: "success.light", borderRadius: 1, px: 2, py: 1, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} color="success.dark">
              Your order is ready for collection!
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 1.5 }} />

        {/* Items */}
        <Stack spacing={0.5} mb={1.5}>
          {(order.items || []).map((item, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                {item.name} × {item.qty}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                R{(item.lineTotal || 0).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: order.note ? 1 : 0 }}>
          <Typography variant="body2" color="text.secondary">
            {order.paymentMethod === "cash_on_delivery" ? "Cash on delivery" : order.paymentMethod}
            {order.orderType ? ` · ${order.orderType}` : ""}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            R{(order.subtotal || 0).toFixed(2)}
          </Typography>
        </Box>

        {order.note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Note: {order.note}
          </Typography>
        )}

        {/* Cancellation reason */}
        {order.status === "cancelled" && order.cancellationReason && (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }} icon={false}>
            <Typography variant="caption" fontWeight={600}>Cancelled by restaurant</Typography>
            <Typography variant="body2">{order.cancellationReason}</Typography>
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5, justifyContent: "flex-end" }}>
          {/* Message restaurant — active orders. Uses restaurantUserId stored
               on the order (new orders), or fetched from restaurant doc (old orders). */}
          {isActive && vendorUserId && (
            <ChatButton
              otherUser={{ id: vendorUserId, type: "vendor", name: order.restaurantName }}
              context="order"
              contextData={{
                restaurantId: order.restaurantId,
                restaurantName: order.restaurantName,
                orderId: order.id,
              }}
              label="Message restaurant"
            />
          )}

          {/* Reorder — completed and cancelled */}
          {(isCompleted || isCancelled) && (
            <Button
              size="small"
              variant="outlined"
              startIcon={reordering ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
              onClick={handleReorder}
              disabled={reordering}
            >
              Reorder
            </Button>
          )}

          {/* Review — completed, within 7 days, not yet reviewed this session */}
          {showReviewPrompt && (
            <Button
              size="small"
              variant={reviewOpen ? "contained" : "outlined"}
              color="primary"
              startIcon={<StarIcon fontSize="small" />}
              onClick={() => setReviewOpen((v) => !v)}
            >
              {reviewOpen ? "Cancel" : "Leave a review"}
            </Button>
          )}

          {/* View restaurant */}
          {order.restaurantId && (
            <Button
              size="small"
              variant="text"
              onClick={() => navigate(`/restaurant/${order.restaurantId}`)}
            >
              View restaurant
            </Button>
          )}
        </Box>

        {/* Review form */}
        <Collapse in={reviewOpen}>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="subtitle2" mb={0.5}>
              How was your experience at {order.restaurantName}?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Your review is for the restaurant overall, not a specific item.
            </Typography>
            <Rating
              value={reviewRating}
              onChange={(_, val) => setReviewRating(val)}
              size="large"
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Food quality, speed, accuracy... (optional)"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                disabled={!reviewRating || submittingReview}
                onClick={handleSubmitReview}
                startIcon={submittingReview ? <CircularProgress size={14} color="inherit" /> : null}
              >
                Submit review
              </Button>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function OrdersSkeleton() {
  return (
    <Stack spacing={2}>
      {[...Array(3)].map((_, i) => (
        <Card key={i} variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Skeleton variant="rounded" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="text" width="30%" />
              </Box>
              <Skeleton variant="rounded" width={80} height={24} />
            </Box>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
